/**
 * AuthModal — Sign In / Sign Up with Social + Email auth
 * Social: Google · GitHub · LinkedIn
 * See SOCIAL_AUTH.md for setup instructions
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, DASHBOARD_ROUTES } from '../context/AuthContext';
import { useSocialAuth } from '../hooks/useSocialAuth';
import { useIntegrations } from '../context/IntegrationsContext';
import { GoogleLogin } from '@react-oauth/google';
import './AuthModal.css';

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  );
}

function GitHubIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z"/>
    </svg>
  );
}

function LinkedInIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="#0A66C2">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
    </svg>
  );
}

function SocialBtn({ provider, label, icon, onClick, loading, disabled, comingSoon }) {
  return (
    <button
      className={`ms-btn ms-btn-${provider}${loading ? ' loading' : ''}${comingSoon ? ' coming-soon' : ''}`}
      onClick={onClick}
      disabled={disabled || loading}
      title={comingSoon ? `${label} — add credentials to .env (see SOCIAL_AUTH.md)` : `Continue with ${label}`}
    >
      {loading ? <span className="ms-spinner" /> : icon}
      <span className="ms-btn-label">{loading ? 'Signing in…' : label}</span>
      {comingSoon && <span className="ms-todo-badge">TODO</span>}
    </button>
  );
}

export default function AuthModal({ open, onClose, showToast }) {
  const [authTab,     setAuthTab]     = useState('signin');
  const role = 'mentee';
  const [email,       setEmail]       = useState('');
  const [password,    setPassword]    = useState('');
  const [firstName,   setFirstName]   = useState('');
  const [lastName,    setLastName]    = useState('');
  const [tcAccepted,  setTcAccepted]  = useState(false);
  const [showHint,    setShowHint]    = useState(true);
  const [showPw,      setShowPw]      = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showConfirmPw,   setShowConfirmPw]   = useState(false);
  const [fieldErrors,     setFieldErrors]     = useState({});
  const [pwTouched,       setPwTouched]       = useState(false);
  const [verifyScreen,setVerifyScreen]= useState(null); // { email } | null
  const [resendSent,  setResendSent]  = useState(false);
  const [resending,   setResending]   = useState(false);
  const [forgotScreen,setForgotScreen]= useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent,  setForgotSent]  = useState(false);
  const [forgotSending,setForgotSending]=useState(false);
  // Photo upload step (shown after successful register, before verify screen)
  const [photoScreen,   setPhotoScreen]   = useState(null);  // { user, pendingVerification }
  const [photoPreview,  setPhotoPreview]  = useState(null);  // data URL
  const [photoUploading,setPhotoUploading]= useState(false);

  const { login, register, updateUser, authError, authLoading, clearError, resendVerification, forgotPassword } = useAuth();

  // ── Password strength ───────────────────────────────────────
  const pwStrength = (() => {
    if (!password) return { score: 0, label: '', color: '' };
    let score = 0;
    if (password.length >= 8)              score++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
    if (/[0-9]/.test(password))            score++;
    if (/[^a-zA-Z0-9]/.test(password))    score++;
    const levels = [
      { label: 'Weak',   color: '#ef4444' },
      { label: 'Weak',   color: '#ef4444' },
      { label: 'Fair',   color: '#f97316' },
      { label: 'Good',   color: '#eab308' },
      { label: 'Strong', color: '#16a34a' },
    ];
    return { score, ...levels[score] };
  })();

  const isEmailValid = v => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v);

  const validateSignup = () => {
    const e = {};
    if (!firstName.trim())  e.firstName = 'First name is required.';
    if (!lastName.trim())   e.lastName  = 'Last name is required.';
    if (!email.trim())      e.email     = 'Email is required.';
    else if (!isEmailValid(email)) e.email = 'Enter a valid email address.';
    if (!password)          e.password  = 'Password is required.';
    else if (pwStrength.score < 2) e.password = 'Password is too weak. Add uppercase, numbers or symbols.';
    if (!confirmPassword)   e.confirmPassword = 'Please confirm your password.';
    else if (confirmPassword !== password) e.confirmPassword = 'Passwords do not match.';
    return e;
  };
  const {
    handleGoogleSuccess,
    handleGoogleError,
    handleGitHub, handleLinkedIn,
    loading: socialLoading, error: socialError, clearError: clearSocial,
  } = useSocialAuth();
  const { isSocialEnabled, getSocialProvider } = useIntegrations();
  const navigate = useNavigate();

  if (!open) return null;

  // Read live admin settings
  const googleEnabled   = isSocialEnabled('google');
  const githubEnabled   = isSocialEnabled('github');
  const linkedinEnabled = isSocialEnabled('linkedin');
  const githubCfg       = getSocialProvider('github');
  const linkedinCfg     = getSocialProvider('linkedin');
  const anySocialVisible = googleEnabled || githubEnabled || linkedinEnabled;

  const switchTab = (t) => { setAuthTab(t); clearError(); clearSocial(); setShowHint(true); setVerifyScreen(null); setResendSent(false); setFieldErrors({}); setConfirmPassword(''); setPwTouched(false); setForgotScreen(false); setForgotSent(false); setForgotEmail(''); };

  const handleResend = async (emailAddr) => {
    setResending(true);
    await resendVerification(emailAddr);
    setResending(false);
    setResendSent(true);
  };

  const handleForgot = async () => {
    if (!forgotEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(forgotEmail)) return;
    setForgotSending(true);
    const ok = await forgotPassword(forgotEmail.trim());
    setForgotSending(false);
    if (ok) setForgotSent(true);
  };

  const handleLogin = async () => {
    const user = await login(email, password);
    if (user) {
      onClose();
      const intent = (() => { try { return JSON.parse(sessionStorage.getItem('db_booking_intent')); } catch { return null; } })();
      if (intent && intent.returnUrl && (Date.now() - intent.savedAt < 30 * 60 * 1000)) {
        setTimeout(() => navigate(intent.returnUrl), 300);
        return;
      }
      showToast('🎉', `Welcome back, ${user.name.split(' ')[0]}!`, `Redirecting to your ${user.role} dashboard…`);
      setTimeout(() => navigate(DASHBOARD_ROUTES[user.role] || '/'), 300);
    }
  };

  const handleRegister = async () => {
    const errs = validateSignup();
    if (Object.keys(errs).length) { setFieldErrors(errs); return; }
    setFieldErrors({});
    if (!tcAccepted) {
      alert('Please accept the Terms of Service and Privacy Policy to continue.');
      return;
    }
    const user = await register({ firstName, lastName, email, password, role,
      termsAcceptedAt: new Date().toISOString() });
    if (user) {
      // Always show photo step first, regardless of verification status
      setPhotoPreview(null);
      setPhotoScreen({ user, pendingVerification: !!user._pendingVerification });
    }
  };

  // Resize & compress selected image to a 300×300 JPEG data URL
  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const SIZE = 300;
        canvas.width = SIZE; canvas.height = SIZE;
        const ctx = canvas.getContext('2d');
        const minSide = Math.min(img.width, img.height);
        const sx = (img.width  - minSide) / 2;
        const sy = (img.height - minSide) / 2;
        ctx.drawImage(img, sx, sy, minSide, minSide, 0, 0, SIZE, SIZE);
        setPhotoPreview(canvas.toDataURL('image/jpeg', 0.85));
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  };

  const _finishPhotoStep = (regUser, pendingVerification) => {
    setPhotoScreen(null);
    setPhotoPreview(null);
    if (pendingVerification) {
      setVerifyScreen({ email: regUser.email });
      return;
    }
    onClose();
    const intent = (() => { try { return JSON.parse(sessionStorage.getItem('db_booking_intent')); } catch { return null; } })();
    if (intent && intent.returnUrl && (Date.now() - intent.savedAt < 30 * 60 * 1000)) {
      setTimeout(() => navigate(intent.returnUrl), 300);
      return;
    }
    if (regUser.role === 'mentor') {
      showToast('🎓', 'Application Submitted!', 'Our team will review and approve your profile within 24 hours.');
    } else {
      showToast('🎉', `Welcome, ${regUser.name.split(' ')[0]}!`, 'Your free session credit is ready!');
      setTimeout(() => navigate(DASHBOARD_ROUTES[regUser.role] || '/'), 300);
    }
  };

  const handlePhotoConfirm = async () => {
    if (!photoScreen) return;
    const { user: regUser, pendingVerification } = photoScreen;
    if (photoPreview) {
      setPhotoUploading(true);
      // TODO §BACKEND: POST /api/users/me/photo { photoUrl }
      await new Promise(r => setTimeout(r, 500));
      if (pendingVerification) {
        // Stash for after email verification + login
        try { sessionStorage.setItem('db_pending_photo', JSON.stringify({ email: regUser.email, photoUrl: photoPreview })); } catch {}
      } else {
        await updateUser({ photoUrl: photoPreview });
      }
      setPhotoUploading(false);
    }
    _finishPhotoStep(regUser, pendingVerification);
  };

  const handlePhotoSkip = () => {
    if (!photoScreen) return;
    _finishPhotoStep(photoScreen.user, photoScreen.pendingVerification);
  };

  // GoogleLogin now handles the popup and calls handleGoogleSuccess

  const handleKey = (e) => {
    if (e.key === 'Enter') authTab === 'signin' ? handleLogin() : handleRegister();
  };

  const anyLoading   = authLoading || !!socialLoading;
  const rawError     = authError || socialError || '';
  const isVerifyError = rawError.startsWith('verify:');
  const verifyErrorEmail = isVerifyError ? rawError.slice(7) : '';
  const displayError = isVerifyError ? '' : rawError;

  return (
    <div className={`modal-overlay ${open ? 'open' : ''}`}>
      <div className="modal-box">

        {/* Header */}
        <div className="modal-head">
          <h3>{forgotScreen ? '🔑 Reset Password' : photoScreen ? '📸 Add a Profile Photo' : verifyScreen ? '📧 Verify Your Email' : authTab === 'signup' ? '🚀 Join DevBrains Free' : '👋 Welcome Back'}</h3>
          <p>{forgotScreen ? "Enter your email and we'll send a reset link." : photoScreen ? 'Step 2 of 2 — Help mentors and peers recognise you.' : verifyScreen ? 'One last step before you get started.' : authTab === 'signup' ? 'Your first session is FREE. No credit card needed.' : 'Sign in to your DevBrains account.'}</p>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="modal-body">

        {/* ── Verify-email screen (post-register or login-blocked) ── */}
        {(verifyScreen || isVerifyError) && (() => {
          const vEmail = verifyScreen?.email || verifyErrorEmail;
          return (
            <div style={{textAlign:'center',padding:'8px 0 4px'}}>
              <div style={{fontSize:52,marginBottom:12}}>📬</div>
              <h3 style={{fontWeight:900,fontSize:'1.15rem',color:'#111827',marginBottom:8}}>Check your inbox</h3>
              <p style={{color:'#6b7280',lineHeight:1.7,marginBottom:6}}>
                We sent a verification link to
              </p>
              <p style={{fontWeight:700,color:'#111827',marginBottom:20,wordBreak:'break-all'}}>{vEmail}</p>
              <p style={{fontSize:'.82rem',color:'#6b7280',marginBottom:24,lineHeight:1.6}}>
                Click the link in the email to activate your account. Check your spam folder if you don't see it.
              </p>
              {resendSent ? (
                <div style={{padding:'10px 16px',background:'#f0fdf4',border:'1px solid #bbf7d0',borderRadius:10,color:'#166534',fontSize:'.84rem',fontWeight:600,marginBottom:16}}>
                  ✅ Verification email resent! Check your inbox.
                </div>
              ) : (
                <button
                  onClick={() => handleResend(vEmail)}
                  disabled={resending}
                  style={{width:'100%',padding:'12px',borderRadius:10,border:'1.5px solid #e5e7eb',background:'#f9fafb',color:'#374151',fontWeight:700,fontSize:'.88rem',cursor:resending?'not-allowed':'pointer',marginBottom:12}}>
                  {resending ? '⏳ Sending…' : '🔁 Resend Verification Email'}
                </button>
              )}
              <button
                onClick={() => { setVerifyScreen(null); clearError(); setResendSent(false); setForgotEmail(vEmail); setForgotScreen(true); }}
                style={{width:'100%',padding:'12px',borderRadius:10,border:'1.5px solid #fde68a',background:'#fffbeb',color:'#92400e',fontWeight:700,fontSize:'.88rem',cursor:'pointer',marginBottom:12}}>
                🔑 Reset Password
              </button>
              <button onClick={() => { setVerifyScreen(null); clearError(); setResendSent(false); switchTab('signin'); }}
                style={{background:'none',border:'none',color:'#16a34a',fontWeight:700,fontSize:'.84rem',cursor:'pointer'}}>
                ← Back to Sign In
              </button>
            </div>
          );
        })()}

        {/* ── Forgot-password screen ── */}
        {forgotScreen && (() => (
          forgotSent ? (
            <div style={{textAlign:'center',padding:'8px 0 4px'}}>
              <div style={{fontSize:52,marginBottom:12}}>📬</div>
              <h3 style={{fontWeight:900,fontSize:'1.15rem',color:'#111827',marginBottom:8}}>Check your inbox</h3>
              <p style={{color:'#6b7280',lineHeight:1.7,marginBottom:6}}>We sent a password reset link to</p>
              <p style={{fontWeight:700,color:'#111827',marginBottom:20,wordBreak:'break-all'}}>{forgotEmail}</p>
              <p style={{fontSize:'.82rem',color:'#6b7280',marginBottom:24,lineHeight:1.6}}>
                Click the link in the email to reset your password. It expires in <strong>30 minutes</strong>.<br/>Check your spam folder if you don't see it.
              </p>
              <button
                onClick={() => { setForgotSent(false); setForgotEmail(''); setForgotScreen(false); }}
                style={{background:'none',border:'none',color:'#16a34a',fontWeight:700,fontSize:'.84rem',cursor:'pointer'}}>
                ← Back to Sign In
              </button>
            </div>
          ) : (
            <div>
              <div className="mfield-wrap">
                <div>
                  <label className="mfield-label">Email Address <span style={{color:'#ef4444'}}>*</span></label>
                  <input
                    className="mfield"
                    type="email"
                    placeholder="you@email.com"
                    value={forgotEmail}
                    onChange={e => { setForgotEmail(e.target.value); clearError(); }}
                    onKeyDown={e => e.key === 'Enter' && handleForgot()}
                    autoFocus
                  />
                </div>
                {authError && <div className="modal-error">⚠️ {authError}</div>}
                <button
                  className="btn btn-primary"
                  style={{width:'100%',justifyContent:'center',padding:'13px',borderRadius:'10px',fontSize:'.9rem',opacity:forgotSending?.5:1}}
                  onClick={handleForgot}
                  disabled={forgotSending}
                >
                  {forgotSending ? '⏳ Sending…' : '📩 Send Reset Link'}
                </button>
                <button
                  onClick={() => { setForgotScreen(false); clearError(); }}
                  style={{display:'block',width:'100%',textAlign:'center',marginTop:8,background:'none',border:'none',color:'#6b7280',fontWeight:700,fontSize:'.82rem',cursor:'pointer'}}>
                  ← Back to Sign In
                </button>
              </div>
            </div>
          )
        ))()}

        {/* ── Photo upload step (post-register) ── */}
        {photoScreen && !forgotScreen && !verifyScreen && !isVerifyError && (() => {
          const regUser = photoScreen.user;
          return (
            <div style={{ textAlign: 'center', padding: '4px 0 8px' }}>
              {/* Avatar preview */}
              <div style={{ position: 'relative', width: 110, height: 110, margin: '0 auto 20px' }}>
                {photoPreview ? (
                  <img
                    src={photoPreview}
                    alt="Preview"
                    style={{ width: 110, height: 110, borderRadius: '50%', objectFit: 'cover', border: '3px solid #1DB954', display: 'block' }}
                  />
                ) : (
                  <div style={{ width: 110, height: 110, borderRadius: '50%', background: `#${regUser.avatarBg || '1DB954'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 36, fontWeight: 800, color: '#fff', border: '3px dashed #d1d5db' }}>
                    {regUser.initials}
                  </div>
                )}
                {/* Camera badge */}
                <label htmlFor="auth-photo-upload" style={{ position: 'absolute', bottom: 4, right: 4, width: 32, height: 32, borderRadius: '50%', background: '#1DB954', border: '2.5px solid #fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 15, boxShadow: '0 2px 8px rgba(0,0,0,.2)' }} title="Choose photo">
                  📷
                </label>
              </div>

              <p style={{ color: '#6b7280', fontSize: '.82rem', lineHeight: 1.6, marginBottom: 20, marginTop: 0 }}>
                A photo helps mentors and peers recognise you.<br />
                <span style={{ color: '#9ca3af' }}>JPG or PNG · Max 5 MB</span>
              </p>

              {/* Hidden file input */}
              <input
                id="auth-photo-upload"
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                style={{ display: 'none' }}
                onChange={handlePhotoChange}
              />

              {/* Choose / Change button */}
              <label
                htmlFor="auth-photo-upload"
                style={{ display: 'block', width: '100%', padding: '11px', borderRadius: 10, border: '1.5px solid #e5e7eb', background: '#f9fafb', color: '#374151', fontWeight: 700, fontSize: '.86rem', cursor: 'pointer', marginBottom: 10, boxSizing: 'border-box' }}
              >
                {photoPreview ? '🔄 Change Photo' : '📂 Choose Photo'}
              </label>

              {/* Continue button — only active when photo is selected */}
              <button
                className="btn btn-primary"
                style={{ width: '100%', justifyContent: 'center', padding: '13px', borderRadius: 10, fontSize: '.9rem', marginBottom: 10, opacity: (!photoPreview || photoUploading) ? .5 : 1 }}
                onClick={handlePhotoConfirm}
                disabled={!photoPreview || photoUploading}
              >
                {photoUploading ? '⏳ Saving photo…' : 'Save & Continue →'}
              </button>

              <button
                onClick={handlePhotoSkip}
                style={{ background: 'none', border: 'none', color: '#9ca3af', fontWeight: 600, fontSize: '.8rem', cursor: 'pointer', padding: '4px 0' }}
              >
                Skip for now
              </button>
            </div>
          );
        })()}

        {!forgotScreen && !verifyScreen && !isVerifyError && !photoScreen && (<>


          {/* Tabs */}
          <div className="modal-auth-tabs">
            <button className={`mtab ${authTab === 'signup' ? 'on' : ''}`} onClick={() => switchTab('signup')}>Sign Up</button>
            <button className={`mtab ${authTab === 'signin' ? 'on' : ''}`} onClick={() => switchTab('signin')}>Sign In</button>
          </div>

          {/* Social buttons — visibility controlled by Admin → Integrations */}
          {anySocialVisible && (
            <>
              <div className="ms-section-label">
                {authTab === 'signup' ? 'Sign up faster with' : 'Sign in with'}
              </div>
              <div className={`modal-social modal-social-${[googleEnabled,githubEnabled,linkedinEnabled].filter(Boolean).length === 1 ? '1' : [googleEnabled,githubEnabled,linkedinEnabled].filter(Boolean).length === 2 ? '2' : '3'}`}>
                {googleEnabled && process.env.REACT_APP_GOOGLE_CLIENT_ID ? (
                  <GoogleLogin
                    onSuccess={async (credentialResponse) => {
                      const user = await handleGoogleSuccess(credentialResponse);
                      if (user) {
                        onClose();
                        showToast('🎉', `Welcome, ${user.name.split(' ')[0]}!`, 'Signed in with Google.');
                        setTimeout(() => navigate(DASHBOARD_ROUTES[user.role] || '/'), 300);
                      }
                    }}
                    onError={handleGoogleError}
                    useOneTap={false}
                    type="standard"
                    theme="outline"
                    size="large"
                    text={authTab === 'signup' ? 'signup_with' : 'signin_with'}
                    shape="rectangular"
                    width="100%"
                  />
                ) : googleEnabled && (
                  <SocialBtn
                    provider="google"
                    label="Google"
                    icon={<GoogleIcon />}
                    onClick={() => {}}
                    loading={socialLoading === 'google'}
                    disabled={anyLoading}
                    comingSoon={!process.env.REACT_APP_GOOGLE_CLIENT_ID}
                  />
                )}
                {githubEnabled && (
                  <SocialBtn
                    provider="github"
                    label="GitHub"
                    icon={<GitHubIcon />}
                    onClick={() => handleGitHub(role)}
                    loading={socialLoading === 'github'}
                    disabled={anyLoading}
                    comingSoon={!githubCfg?.config?.clientId}
                  />
                )}
                {linkedinEnabled && (
                  <SocialBtn
                    provider="linkedin"
                    label="LinkedIn"
                    icon={<LinkedInIcon />}
                    onClick={() => handleLinkedIn(role)}
                    loading={socialLoading === 'linkedin'}
                    disabled={anyLoading}
                    comingSoon={!linkedinCfg?.config?.clientId}
                  />
                )}
              </div>
              <div className="modal-divider">or continue with email</div>
            </>
          )}

          {/* Demo hint — only in mock mode */}
          {process.env.REACT_APP_MOCK !== 'false' && authTab === 'signin' && showHint && (
            <div className="demo-login-section">
              <div className="demo-login-label">🔑 Demo Accounts — click to fill</div>
              <div className="demo-btns">
                <button className="demo-btn mentor" onClick={() => { setEmail('mentor@devbrains.com'); setPassword('mentor123'); setShowHint(false); }}>💼 Mentor</button>
                <button className="demo-btn admin"  onClick={() => { setEmail('admin@devbrains.com');  setPassword('admin123');  setShowHint(false); }}>⚙️ Admin</button>
              </div>
            </div>
          )}

          {/* Fields */}
          <div className="mfield-wrap">
            {authTab === 'signup' && (
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                <div>
                  <label className="mfield-label">First Name <span style={{color:'#ef4444'}}>*</span></label>
                  <input className="mfield" type="text" placeholder="Rahul" value={firstName}
                    style={{borderColor: fieldErrors.firstName ? '#fca5a5' : ''}}
                    onChange={e => { setFirstName(e.target.value); setFieldErrors(p => ({...p, firstName: undefined})); }}
                    onKeyDown={handleKey} />
                  {fieldErrors.firstName && <p style={{fontSize:'.72rem',color:'#ef4444',marginTop:3}}>{fieldErrors.firstName}</p>}
                </div>
                <div>
                  <label className="mfield-label">Last Name <span style={{color:'#ef4444'}}>*</span></label>
                  <input className="mfield" type="text" placeholder="Singh" value={lastName}
                    style={{borderColor: fieldErrors.lastName ? '#fca5a5' : ''}}
                    onChange={e => { setLastName(e.target.value); setFieldErrors(p => ({...p, lastName: undefined})); }}
                    onKeyDown={handleKey} />
                  {fieldErrors.lastName && <p style={{fontSize:'.72rem',color:'#ef4444',marginTop:3}}>{fieldErrors.lastName}</p>}
                </div>
              </div>
            )}
            <div>
              <label className="mfield-label">Email Address <span style={{color:'#ef4444'}}>*</span></label>
              <div style={{position:'relative'}}>
                <input className="mfield" type="email" placeholder="you@email.com" value={email}
                  style={{borderColor: fieldErrors.email ? '#fca5a5' : (email && isEmailValid(email) && authTab==='signup' ? '#86efac' : ''), paddingRight: authTab==='signup' && email ? 36 : undefined}}
                  onChange={e => { setEmail(e.target.value); setFieldErrors(p => ({...p, email: undefined})); }}
                  onKeyDown={handleKey} />
                {authTab === 'signup' && email && (
                  <span style={{position:'absolute',right:12,top:'50%',transform:'translateY(-50%)',fontSize:'.88rem'}}>
                    {isEmailValid(email) ? '✅' : '❌'}
                  </span>
                )}
              </div>
              {fieldErrors.email && <p style={{fontSize:'.72rem',color:'#ef4444',marginTop:3}}>{fieldErrors.email}</p>}
            </div>
            <div style={{ position:'relative' }}>
              <label className="mfield-label">Password <span style={{color:'#ef4444'}}>*</span></label>
              <input className="mfield" type={showPw ? 'text' : 'password'}
                placeholder={authTab === 'signup' ? 'Create a strong password' : 'Your password'}
                value={password}
                style={{borderColor: fieldErrors.password ? '#fca5a5' : '', paddingRight:44}}
                onChange={e => { setPassword(e.target.value); setPwTouched(true); setFieldErrors(p => ({...p, password: undefined})); }}
                onKeyDown={handleKey} />
              <button type="button" onClick={() => setShowPw(v => !v)} style={{ position:'absolute', right:12, top:32, background:'none', border:'none', cursor:'pointer', color:'#9ca3af', padding:2 }}>
                {showPw ? '🙈' : '👁'}
              </button>
              {/* Password strength bar — only on signup */}
              {authTab === 'signup' && pwTouched && password && (
                <div style={{marginTop:8}}>
                  <div style={{display:'flex',gap:4,marginBottom:4}}>
                    {[1,2,3,4].map(i => (
                      <div key={i} style={{flex:1,height:4,borderRadius:4,transition:'background .2s',
                        background: i <= pwStrength.score ? pwStrength.color : '#e5e7eb'}} />
                    ))}
                  </div>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                    <span style={{fontSize:'.7rem',fontWeight:700,color:pwStrength.color}}>{pwStrength.label}</span>
                    <span style={{fontSize:'.68rem',color:'#9ca3af'}}>
                      {[
                        password.length >= 8       && '8+ chars',
                        /[A-Z]/.test(password)     && 'Uppercase',
                        /[0-9]/.test(password)     && 'Number',
                        /[^a-zA-Z0-9]/.test(password) && 'Symbol',
                      ].filter(Boolean).join(' · ')}
                    </span>
                  </div>
                </div>
              )}
              {fieldErrors.password && <p style={{fontSize:'.72rem',color:'#ef4444',marginTop:3}}>{fieldErrors.password}</p>}
            </div>

            {/* Confirm password — signup only */}
            {authTab === 'signup' && (
              <div style={{position:'relative'}}>
                <label className="mfield-label">Confirm Password <span style={{color:'#ef4444'}}>*</span></label>
                <input className="mfield" type={showConfirmPw ? 'text' : 'password'}
                  placeholder="Re-enter your password"
                  value={confirmPassword}
                  style={{borderColor: fieldErrors.confirmPassword ? '#fca5a5' : (confirmPassword && confirmPassword === password ? '#86efac' : ''), paddingRight:44}}
                  onChange={e => { setConfirmPassword(e.target.value); setFieldErrors(p => ({...p, confirmPassword: undefined})); }}
                  onKeyDown={handleKey} />
                <button type="button" onClick={() => setShowConfirmPw(v => !v)} style={{ position:'absolute', right:12, top:32, background:'none', border:'none', cursor:'pointer', color:'#9ca3af', padding:2 }}>
                  {showConfirmPw ? '🙈' : '👁'}
                </button>
                {confirmPassword && (
                  <p style={{fontSize:'.72rem',marginTop:3,fontWeight:600,color: confirmPassword === password ? '#16a34a' : '#ef4444'}}>
                    {confirmPassword === password ? '✓ Passwords match' : '✗ Passwords do not match'}
                  </p>
                )}
                {fieldErrors.confirmPassword && !confirmPassword && <p style={{fontSize:'.72rem',color:'#ef4444',marginTop:3}}>{fieldErrors.confirmPassword}</p>}
              </div>
            )}

            {authTab === 'signup' && (
              <div style={{display:'flex',alignItems:'flex-start',gap:10,padding:'10px 14px',background:'#eff6ff',borderRadius:10,border:'1px solid #bfdbfe'}}>
                <span style={{fontSize:'1rem',flexShrink:0}}>🎓</span>
                <p style={{fontSize:'.78rem',color:'#1e40af',lineHeight:1.5,margin:0}}>
                  You'll join as a <strong>Learner</strong>. Want to become a mentor?{' '}
                  <a href="/become-mentor" onClick={onClose} style={{color:'#1d4ed8',fontWeight:700,textDecoration:'underline'}}>Apply here</a>{' '}after signing up.
                </p>
              </div>
            )}

            {displayError && <div className="modal-error">⚠️ {displayError}</div>}

            <button
              className="btn btn-primary"
              style={{ width:'100%', justifyContent:'center', padding:'13px', borderRadius:'10px', fontSize:'.9rem',
                opacity: (anyLoading || (authTab === 'signup' && !tcAccepted)) ? .5 : 1,
                cursor:  authTab === 'signup' && !tcAccepted ? 'not-allowed' : 'pointer' }}
              onClick={authTab === 'signin' ? handleLogin : handleRegister}
              disabled={anyLoading || (authTab === 'signup' && !tcAccepted)}
              title={authTab === 'signup' && !tcAccepted ? 'Please accept Terms & Privacy Policy first' : ''}
            >
              {authLoading ? '⏳ Please wait…' : authTab === 'signup' ? 'Create Free Account →' : 'Sign In →'}
            </button>
          </div>

          {authTab === 'signin' && (
            <div style={{ textAlign:'center', marginTop:10 }}>
              <button
                type="button"
                onClick={() => { setForgotScreen(true); setForgotEmail(email); clearError(); }}
                style={{background:'none',border:'none',fontSize:'.78rem',color:'var(--green-d)',fontWeight:700,cursor:'pointer',padding:0}}
              >Forgot password?</button>
            </div>
          )}
          {authTab === 'signup' && (
            <div style={{ display:'flex', alignItems:'flex-start', gap:10, padding:'12px 14px', background:'#f0fdf4', borderRadius:10, border:'1px solid #bbf7d0', marginTop:4 }}>
              <input
                id="tc-accept"
                type="checkbox"
                checked={tcAccepted}
                onChange={e => setTcAccepted(e.target.checked)}
                style={{ marginTop:3, accentColor:'#1DB954', width:16, height:16, flexShrink:0, cursor:'pointer' }}
              />
              <label htmlFor="tc-accept" style={{ fontSize:'.78rem', color:'#374151', lineHeight:1.5, cursor:'pointer' }}>
                I have read and agree to the{' '}
                <a href="/terms" target="_blank" style={{ color:'#16a34a', fontWeight:700 }}>Terms of Service</a>
                {' '}and{' '}
                <a href="/privacy" target="_blank" style={{ color:'#16a34a', fontWeight:700 }}>Privacy Policy</a>.
                {' '}Your data will be handled in accordance with our privacy policy.
              </label>
            </div>
          )}
        </>)}
        </div>
      </div>
    </div>
  );
}
